import { useRefreshBlocker } from '@speaking-coach/ui'
import { SectionHeader } from '../common/section-header'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import { Button, buttonVariants, Checkbox, FieldHelp, Input, Notice } from '@speaking-coach/ui'
import { adminHrUsersApi } from '../hr_users/api'
import type { HrUserListResponse } from '../hr_users/types'
import { adminCohortClient, messageForCohortError } from './api'
import type { CohortDetail, CohortHrAccess } from './types'

const SEARCH_DEBOUNCE_MS = 250
const SEARCH_LIMIT = 20

function equalIds(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false
  const rightSet = new Set(right)
  return left.every((value) => rightSet.has(value))
}

export function CohortHrAccessSection({
  cohort,
  clientId,
}: {
  cohort: CohortDetail
  clientId: string
}): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [draftIds, setDraftIds] = useState<string[] | null>(null)
  const [confirmation, setConfirmation] = useState<string | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const params = {
    clientId,
    search: search || undefined,
    limit: SEARCH_LIMIT,
    offset: 0,
  }
  const hrUsersQuery = useQuery({
    queryKey: queryKeys.hrUserList(params),
    queryFn: () => adminHrUsersApi.list(params),
    placeholderData: keepPreviousData,
  })
  const accessQuery = useQuery({
    queryKey: queryKeys.cohortHrAccess(clientId, cohort.cohortId),
    queryFn: () => adminCohortClient.getHrAccess(clientId, cohort.cohortId),
  })

  useEffect(() => {
    if (accessQuery.data !== undefined && draftIds === null) {
      setDraftIds(accessQuery.data.hrUserIds)
    }
  }, [accessQuery.data, draftIds])

  const selected = useMemo(() => new Set(draftIds ?? []), [draftIds])
  const storedIds = accessQuery.data?.hrUserIds ?? []
  const changed = draftIds !== null && !equalIds(draftIds, storedIds)
  useRefreshBlocker(changed)

  const saveAccess = useMutation({
    mutationFn: (hrUserIds: string[]) =>
      adminCohortClient.setHrAccess(clientId, cohort.cohortId, hrUserIds),
    onSuccess: (access: CohortHrAccess) => {
      setDraftIds(access.hrUserIds)
      queryClient.setQueryData(
        queryKeys.cohortHrAccess(clientId, cohort.cohortId),
        access,
      )
      const allowed = new Set(access.hrUserIds)
      queryClient.setQueriesData<HrUserListResponse>(
        { queryKey: queryKeys.hrUsers },
        (current) =>
          current
            ? {
                ...current,
                hrUsers: current.hrUsers.map((row) => ({
                  ...row,
                  cohortIds: allowed.has(row.userId)
                    ? [...new Set([...row.cohortIds, cohort.cohortId])]
                    : row.cohortIds.filter((id) => id !== cohort.cohortId),
                })),
              }
            : current,
      )
      setConfirmation(
        `${access.hrUserIds.length} HR ${access.hrUserIds.length === 1 ? 'user can' : 'users can'} now see ${cohort.name}.`,
      )
    },
  })

  const createHrUserUrl =
    `/admin/clients/${clientId}/hr-users/new?` +
    new URLSearchParams({ cohortId: cohort.cohortId }).toString()
  const hrUsers = hrUsersQuery.data?.hrUsers ?? []
  const total = hrUsersQuery.data?.total ?? 0
  const selectedShown = hrUsers.filter((row) => selected.has(row.userId)).length
  // PROV-HRALL: an all-cohort HR user holds every cohort by rule; the server
  // refuses a set that drops one (409). The FULL locked set comes with the
  // access read — not only the rows the current search page shows —
  // otherwise "Clear all" with a locked user filtered out of view submitted
  // a set the server could only refuse, taking the intended removals down
  // with it. Visible flagged rows are unioned in for rows newer than the
  // access read.
  const locked = useMemo(
    () =>
      new Set([
        ...(accessQuery.data?.lockedHrUserIds ?? []),
        ...hrUsers.filter((row) => row.hrAllCohortAccess).map((row) => row.userId),
      ]),
    [accessQuery.data?.lockedHrUserIds, hrUsers],
  )

  function toggle(userId: string): void {
    if (locked.has(userId)) return
    setConfirmation(null)
    saveAccess.reset()
    setDraftIds((current) => {
      const next = new Set(current ?? storedIds)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return [...next].sort()
    })
  }

  function selectShown(): void {
    setDraftIds((current) =>
      [...new Set([...(current ?? storedIds), ...hrUsers.map((row) => row.userId)])].sort(),
    )
  }

  return (
    <section aria-label="HR access" className="mt-4 rounded-token-card bg-surface-card p-6">
      <SectionHeader
        title="HR access"
        meta={<FieldHelp label="cohort HR access">
            Choose everyone who should see this cohort, then apply the complete
            set once. Existing selections remain checked across searches.
          </FieldHelp>}
        actions={<Link
          to={createHrUserUrl}
          className={buttonVariants({ variant: 'secondary', size: 'sm' })}
        >
          Add new HR user
        </Link>}
      />

      <label
        htmlFor="cohort-hr-user-search"
        className="mt-4 block text-caption font-medium"
      >
        Search existing HR users
      </label>
      <Input
        id="cohort-hr-user-search"
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setConfirmation(null)
        }}
        placeholder={`Search ${cohort.clientName} HR users by name or email...`}
        className="mt-1"
        autoComplete="off"
      />

      {confirmation && (
        <p role="status" className="mt-3 text-body text-text-secondary">
          {confirmation}
        </p>
      )}
      {(saveAccess.error || accessQuery.error) && (
        <Notice variant="danger" className="mt-3">
          {messageForCohortError(saveAccess.error ?? accessQuery.error)}
        </Notice>
      )}

      {hrUsers.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={selectedShown === hrUsers.length}
            onClick={selectShown}
          >
            Select all shown
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={(draftIds ?? storedIds).length === 0}
            onClick={() =>
              // Locked rows survive "clear": they cannot be dropped anyway.
              setDraftIds((current) =>
                [...new Set(current ?? storedIds)].filter((id) => locked.has(id)).sort(),
              )
            }
          >
            Clear all access
          </Button>
          <span className="text-caption text-text-secondary">
            {(draftIds ?? storedIds).length} selected
          </span>
        </div>
      )}

      <div
        aria-label="HR users for this client"
        className={
          'mt-3 space-y-2 transition-opacity ' +
          (hrUsersQuery.isPlaceholderData ? 'opacity-60' : '')
        }
      >
        {(hrUsersQuery.isPending || accessQuery.isPending) && (
          <p className="text-body text-text-secondary">Loading HR access…</p>
        )}
        {hrUsersQuery.isError && (
          <p role="alert" className="text-body text-danger-text">
            HR users could not be loaded. Try again.
          </p>
        )}
        {hrUsersQuery.isSuccess && hrUsers.length === 0 && (
          <p className="rounded-token-card border border-dashed border-border-default p-3 text-body text-text-secondary">
            {search === ''
              ? `${cohort.clientName} has no HR users yet.`
              : 'No HR users match that search.'}
          </p>
        )}
        {hrUsers.map((hrUser) => (
          <label
            key={hrUser.userId}
            className="flex cursor-pointer items-center gap-3 rounded-token-card border border-border-default bg-surface-card-inset p-3"
          >
            <Checkbox
              checked={locked.has(hrUser.userId) || selected.has(hrUser.userId)}
              disabled={
                accessQuery.isPending || saveAccess.isPending || locked.has(hrUser.userId)
              }
              onCheckedChange={() => toggle(hrUser.userId)}
            />
            <span className="min-w-0">
              <span className="block text-body font-medium">{hrUser.name}</span>
              <span className="block truncate text-caption text-text-secondary">
                {hrUser.email}
              </span>
            </span>
            {locked.has(hrUser.userId) && (
              <span className="ml-auto shrink-0 text-caption text-text-secondary">
                All-cohort access
              </span>
            )}
          </label>
        ))}
        {total > hrUsers.length && (
          <div className="rounded-token-card border border-dashed border-border-default px-3 py-2 text-center text-caption text-text-secondary">
            Showing {hrUsers.length} of {total}. Search to find others; your
            selections remain checked across searches.
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-border-default pt-4">
        <Button
          type="button"
          variant="ghost"
          disabled={!changed || saveAccess.isPending}
          onClick={() => setDraftIds(storedIds)}
        >
          Discard changes
        </Button>
        <Button
          type="button"
          disabled={!changed || saveAccess.isPending}
          onClick={() => saveAccess.mutate(draftIds ?? storedIds)}
        >
          {saveAccess.isPending ? 'Applying…' : 'Apply HR access'}
        </Button>
      </div>
    </section>
  )
}
